import { Box, Typography } from '@mui/material'
import { MButton, ProgressLoaders, SvgImage } from 'components'
import { ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { getMetricUnit } from 'containers/Usage/components/utils'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type IPast12MonthsUsageData,
  type UtilityType
} from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { DashboardContainer } from './DashboardContainer'
import UtilityItem from './UtilityItem'
import { formatValue } from 'utils'

interface IUtilityItems {
  type: UtilityType
  cost: string | number
  highestThisYear?: number
}

interface INumberStats {
  past12MonthsUsageData: IPast12MonthsUsageData[]
  isLoading: boolean
}

export const NumberStats = ({
  past12MonthsUsageData,
  isLoading
}: INumberStats): JSX.Element => {
  const navigate = useNavigate()
  const isMobile = useMobileBreakpoints()
  const [utilityItems, setUtilityItems] = useState<IUtilityItems[]>([])

  const fetchNumberStats = async (): Promise<void> => {
    const result = past12MonthsUsageData.map((usage: any) => {
      const usageData = usage?.usage?.usageHistory?.[0]?.usageData ?? []
      const usageThisMonth =
        usageData?.length > 0 ? usageData[usageData.length - 1]?.value ?? 0 : 0

      const highestThisYear = Math.max(
        ...usageData?.map((usage: { value: string }) => usage.value) ?? 0
      )

      return { type: usage.service, cost: usageThisMonth, highestThisYear }
    })

    setUtilityItems(result)
  }

  useEffect(() => {
    void fetchNumberStats()
  }, [past12MonthsUsageData])

  if (isLoading) {
    return (
      <DashboardContainer
        minHeight={past12MonthsUsageData.length === 1 ? '26vh' : '35vh'}
      >
        <ProgressLoaders height="100%" />
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer
      minHeight={past12MonthsUsageData.length === 1 ? '26vh' : '35vh'}
    >
      <Box>
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box>
            <Typography color={color.black} fontSize={18} fontWeight={700}>
              This Month Usage
            </Typography>
            <Typography color={color.grey[700]} fontSize={12}>
              {dayjs().format('MMMM YYYY')}
            </Typography>
          </Box>

          {past12MonthsUsageData.length !== 2 && (
            <MButton
              variant="outlined"
              size="large"
              rounded="true"
              texttransform="none"
              onClick={() => {
                navigate(ROUTES_PATH.USAGE)
              }}
            >
              View All
            </MButton>
          )}
        </Box>

        {past12MonthsUsageData.length === 1
          ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                borderRadius={1}
                border={`solid 1px ${color.background[300]}`}
                width={isMobile ? 'auto' : '90%'}
                gap={2}
                p={2}
              >
                <SvgImage name="UsageThisMonthIcon" width={50} height={50} />

                <Box display="flex" flexDirection="column">
                  <Box>
                    <Typography
                      display="inline"
                      fontSize={18}
                      fontWeight={700}
                      color={color.grey[900]}
                    >
                      {formatValue(String(utilityItems[0]?.cost ?? 0), true, false)}
                    </Typography>
                    <Typography display="inline">&nbsp;</Typography>

                    <Typography
                      display="inline"
                      fontSize={16}
                      fontWeight={700}
                      color={color.grey[700]}
                    >
                      {getMetricUnit(utilityItems[0]?.type.toLowerCase())}
                    </Typography>
                  </Box>

                  <Typography
                    fontSize={10}
                    letterSpacing={1}
                    color={color.grey[700]}
                    textTransform="uppercase"
                  >
                    Total Usage This Month
                  </Typography>
                </Box>
              </Box>

              <Box
                display="flex"
                borderRadius={1}
                border={`solid 1px ${color.background[300]}`}
                width={isMobile ? 'auto' : '90%'}
                gap={2}
                p={2}
              >
                <SvgImage name="HighestThisYearIcon" width={50} height={50} />

                <Box display="flex" flexDirection="column">
                  <Box>
                    <Typography
                      display="inline"
                      fontSize={18}
                      fontWeight={700}
                      color={color.grey[900]}
                    >
                      {formatValue(String(utilityItems[0]?.highestThisYear ?? 0), true, false)}
                    </Typography>
                    <Typography display="inline">&nbsp;</Typography>

                    {
                      <Typography
                        display="inline"
                        fontSize={16}
                        fontWeight={700}
                        color={color.grey[700]}
                      >
                        {getMetricUnit(utilityItems[0]?.type.toLowerCase())}
                      </Typography>
                    }
                  </Box>

                  <Typography
                    fontSize={10}
                    letterSpacing={1}
                    color={color.grey[700]}
                    textTransform="uppercase"
                  >
                    Highest This Year
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
          : (
            <Box display="flex" flexDirection="column" gap={2}>
              {utilityItems?.map((item, index) => (
                <UtilityItem
                  key={index}
                  type={item.type.toLowerCase()}
                  cost={item.cost}
                  label={`${item.type} USAGE`}
                  metric={true}
                />
              ))}
            </Box>
          )}
      </Box>

      {past12MonthsUsageData.length === 2 && (
        <Box display="flex" flexDirection="column" mt={2}>
          <MButton
            variant="outlined"
            size="large"
            rounded="true"
            texttransform="none"
            onClick={() => {
              navigate(ROUTES_PATH.USAGE)
            }}
          >
            View All
          </MButton>
        </Box>
      )}
    </DashboardContainer>
  )
}
